import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSites, useSite } from '../hooks/useSites';
import { useContent } from '../hooks/useContent';
import { TopziteOreIcon } from './TopziteOreIcon';
import { api } from '../api/client';
import Sidebar from './Sidebar';
import ToastContainer from './Toast';
import LayoutBody from './LayoutBody';
import { GlobalPreviewProvider } from '../hooks/useGlobalPreview';
import type { SiteStatus } from '../types';

// ─── Publish Status Types ─────────────────────────────────────────

type PublishState =
  | 'draft'           // never published
  | 'live'            // live, no changes
  | 'live-changes'    // live, has unpublished changes
  | 'building'        // currently building
  | 'error'           // last deploy failed
  | 'just-published'; // transient: just went live

// ─── Publish Status Bar ───────────────────────────────────────────

interface StatusBarProps {
  publishState: PublishState;
  subdomain: string | null;
  siteUrl: string | null;
  onPublish: () => void;
  buildProgress: number;
}

function PublishStatusBar({
  publishState,
  subdomain,
  siteUrl,
  onPublish,
  buildProgress,
}: StatusBarProps) {
  const stateConfig: Record<
    PublishState,
    { className: string; text: string; action?: string }
  > = {
    draft: {
      className: 'status-bar-draft',
      text: "Your site hasn't been published yet",
      action: 'Publish Now',
    },
    live: {
      className: 'status-bar-live',
      text: subdomain
        ? `Your site is live at ${subdomain}.topzite.net`
        : 'Your site is live',
    },
    'live-changes': {
      className: 'status-bar-changes',
      text: 'You have unpublished changes',
      action: 'Publish',
    },
    building: {
      className: 'status-bar-building',
      text: 'Publishing your site…',
    },
    error: {
      className: 'status-bar-error',
      text: 'Publishing failed',
      action: 'Try Again',
    },
    'just-published': {
      className: 'status-bar-just-published',
      text: 'Your site is live!',
    },
  };

  const config = stateConfig[publishState];

  return (
    <div
      className={`publish-status-bar ${config.className}`}
      role="status"
      aria-live={publishState === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className="publish-status-bar-content">
        <span className="publish-status-bar-text">
          {publishState === 'building' && (
            <span className="status-bar-spinner" aria-hidden="true" />
          )}
          {config.text}
        </span>

        <span className="publish-status-bar-actions">
          {/* Link to live site for live states */}
          {(publishState === 'live' || publishState === 'just-published') && siteUrl && (
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="status-bar-link"
            >
              Visit site ↗
            </a>
          )}

          {/* Action button */}
          {config.action && (
            <button className="status-bar-btn" onClick={onPublish}>
              {config.action}
            </button>
          )}
        </span>
      </div>

      {/* Inline progress bar for building state */}
      {publishState === 'building' && (
        <div className="status-bar-progress">
          <div
            className="status-bar-progress-fill"
            style={{ width: `${buildProgress}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────

export default function Layout() {
  const { user, logout } = useAuth();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sites, loading: sitesLoading } = useSites();
  const { site, refresh: refreshSite } = useSite(siteId);

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  const currentSite = sites.find((s) => s.siteId === siteId);

  // Fetch site content for sectionLabel
  const { items: siteContentItems } = useContent(siteId, 'site');
  const siteContent = siteContentItems[0]?.data as Record<string, unknown> | undefined;
  const sectionLabel = (siteContent?.sectionLabel as string) || 'Products';

  // ─── Deploy / Status State ───────────────────────────────────

  const [hasChanges, setHasChanges] = useState(false);
  const [neverDeployed, setNeverDeployed] = useState(false);
  const [previewChecked, setPreviewChecked] = useState<string | null>(null);
  const [justPublished, setJustPublished] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const buildStartRef = useRef<number | null>(null);

  // ─── Billing Plan Status ─────────────────────────────────────
  const [planStatus, setPlanStatus] = useState<{
    plan: 'trial' | 'friend' | 'active';
    daysLeft: number | null;
    expired: boolean;
  } | null>(null);

  // Derive the site status from the Site object
  const siteStatus: SiteStatus = site?.status || currentSite?.status || 'draft';

  // Compute the publish state for the status bar
  const publishState: PublishState = (() => {
    if (justPublished) return 'just-published';
    if (siteStatus === 'building') return 'building';
    if (siteStatus === 'error') return 'error';
    if (neverDeployed || siteStatus === 'draft') return 'draft';
    if (hasChanges) return 'live-changes';
    return 'live';
  })();

  // ─── Check for Changes ──────────────────────────────────────

  const checkChanges = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await api.getDeployPreview(siteId);
      const total = data.changes.created.length + data.changes.modified.length;
      setHasChanges(data.neverDeployed || total > 0);
      setNeverDeployed(data.neverDeployed);
      setPreviewChecked(siteId);
    } catch {
      setHasChanges(false);
    }
  }, [siteId]);

  // Initial check when site changes
  useEffect(() => {
    if (siteId && siteId !== previewChecked) {
      checkChanges();
    }
  }, [siteId, previewChecked, checkChanges]);

  // Re-check changes on route navigation (catches saves)
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      if (siteId) {
        // Small delay to let any save finish
        const timer = setTimeout(() => checkChanges(), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, siteId, checkChanges]);

  // ─── Poll While Building ────────────────────────────────────

  useEffect(() => {
    if (publishState !== 'building') {
      // Reset build progress when not building
      if (buildStartRef.current) {
        buildStartRef.current = null;
        setBuildProgress(0);
      }
      return;
    }

    // Start tracking build time
    if (!buildStartRef.current) {
      buildStartRef.current = Date.now();
    }

    // Progress animation — asymptotic curve so it never visibly stalls
    const progressTimer = setInterval(() => {
      if (buildStartRef.current) {
        const elapsed = Date.now() - buildStartRef.current;
        // Approaches 98% asymptotically (63% at 30s, 86% at 60s, 95% at 90s)
        const pct = Math.floor(98 * (1 - Math.exp(-elapsed / 30000)));
        setBuildProgress(pct);
      }
    }, 500);

    // Poll site status every 5 seconds
    const pollTimer = setInterval(() => {
      refreshSite();
    }, 5000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(pollTimer);
    };
  }, [publishState, refreshSite]);

  // Detect transition from building → live
  const prevStatusRef = useRef<SiteStatus>(siteStatus);
  useEffect(() => {
    if (prevStatusRef.current === 'building' && siteStatus === 'live') {
      // Build just finished successfully
      setBuildProgress(100);
      setJustPublished(true);
      setHasChanges(false);
      setNeverDeployed(false);

      // Auto-transition after 10 seconds
      const timer = setTimeout(() => {
        setJustPublished(false);
      }, 10000);

      // Re-check changes
      checkChanges();

      prevStatusRef.current = siteStatus;
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = siteStatus;
  }, [siteStatus, checkChanges]);

  // ─── Publish Handler ────────────────────────────────────────

  // Publishing always goes through the Publish page, which shows the change
  // summary and offers a preview first — one path, no surprise deploys.
  const goToPublish = () => {
    if (siteId) navigate(`/sites/${siteId}/publish`);
  };

  // ─── UI Handlers ────────────────────────────────────────────

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setSelectorOpen(false);
      }
    };
    if (selectorOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [selectorOpen]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch billing plan once on mount
  useEffect(() => {
    api.getBillingPlan().then(info => {
      const expired = info.plan === 'trial' && info.trialExpiresAt
        ? new Date(info.trialExpiresAt) < new Date() : false;
      const daysLeft = info.plan === 'trial' && info.trialExpiresAt
        ? Math.max(0, Math.ceil((new Date(info.trialExpiresAt).getTime() - Date.now()) / 86400000))
        : null;
      setPlanStatus({ plan: info.plan, daysLeft, expired });
    }).catch(() => {}); // Silent — UI degrades gracefully without plan status
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSiteSwitch = (newSiteId: string) => {
    setSelectorOpen(false);
    navigate(`/sites/${newSiteId}`);
  };

  // ─── Topbar Publish Button ──────────────────────────────────

  const renderTopbarPublishBtn = () => {
    if (!siteId) return null;

    const isBuilding = publishState === 'building';
    const isUpToDate = publishState === 'live' || publishState === 'just-published';
    const showDot = publishState === 'live-changes' || publishState === 'draft';
    const siteUrl = site?.siteUrl || currentSite?.siteUrl || null;

    // When up to date, show a link to the live site instead of a publish button
    if (isUpToDate && siteUrl) {
      return (
        <a
          href={siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="topbar-publish-btn topbar-publish-btn-muted"
          title="Your site is up to date — view it live"
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
          Published ✓
        </a>
      );
    }

    let label = 'Publish';
    if (isBuilding) label = 'Publishing…';

    return (
      <button
        className="topbar-publish-btn"
        onClick={goToPublish}
        disabled={isBuilding}
        title={
          isBuilding
            ? 'Publishing in progress'
            : hasChanges
              ? 'You have unpublished changes'
              : 'Publish your site'
        }
      >
        {showDot && <span className="publish-dot" aria-hidden="true" />}
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
        {label}
      </button>
    );
  };

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="layout">
      <header className="topbar">
        {/* Mobile menu toggle — only visible on small screens when viewing a site */}
        {siteId && (
          <button
            className="topbar-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="sidebar-nav"
          >
            <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        )}

        <NavLink to="/dashboard" className="topbar-brand">
          <TopziteOreIcon size={24} />
          <span className="topbar-brand-text">top<span className="topbar-brand-accent">zite</span></span>
        </NavLink>

        {/* Site selector / name — only when viewing a site */}
        {siteId && !sitesLoading && currentSite && (
          <div className="site-selector" ref={selectorRef}>
            {sites.length === 1 ? (
              <span className="site-selector-name">{currentSite.siteName}</span>
            ) : (
              <>
                <button
                  className="site-selector-btn"
                  onClick={() => setSelectorOpen(!selectorOpen)}
                  aria-expanded={selectorOpen}
                  aria-haspopup="listbox"
                  aria-label={`Switch site, currently ${currentSite.siteName}`}
                >
                  <span>{currentSite.siteName}</span>
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M3 5l3 3 3-3" />
                  </svg>
                </button>
                {selectorOpen && (
                  <div className="site-selector-dropdown" role="listbox" aria-label="Your sites">
                    {sites.map((s) => (
                      <button
                        key={s.siteId}
                        role="option"
                        aria-selected={s.siteId === siteId}
                        className={`site-selector-option ${s.siteId === siteId ? 'active' : ''}`}
                        onClick={() => handleSiteSwitch(s.siteId)}
                      >
                        <span className="site-selector-option-name">{s.siteName}</span>
                        <span className="site-selector-option-sub">{s.subdomain}.topzite.net</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="topbar-user">
          {renderTopbarPublishBtn()}
          <span className="topbar-email">{user?.email}</span>
          <span className="topbar-billing-wrapper">
            <NavLink to="/billing" className="topbar-help-link" title="Billing" aria-label="Billing">
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            </NavLink>
            {planStatus?.plan === 'trial' && (
              <span className={`topbar-billing-dot ${planStatus.expired ? 'topbar-billing-dot--expired' : 'topbar-billing-dot--trial'}`} />
            )}
          </span>
          <a
            href="https://www.topzite.net/help.html"
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-help-link"
            title="Help & FAQ"
          >
            ?
          </a>
          <button className="btn-text" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </header>

      {/* ─── Global Publish Status Bar ───────────────────────── */}
      {siteId && (
        <PublishStatusBar
          publishState={publishState}
          subdomain={currentSite?.subdomain || site?.subdomain || null}
          siteUrl={site?.siteUrl || currentSite?.siteUrl || null}
          onPublish={goToPublish}
          buildProgress={buildProgress}
        />
      )}

      <GlobalPreviewProvider siteId={siteId} subdomain={currentSite?.subdomain || site?.subdomain || null}>
      <LayoutBody siteId={siteId} sidebar={
        siteId ? (
          <>
            {mobileMenuOpen && (
              <div
                className="sidebar-overlay"
                onClick={() => setMobileMenuOpen(false)}
              />
            )}
            <div id="sidebar-nav" className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <Sidebar siteId={siteId} sectionLabel={sectionLabel} planStatus={planStatus} />
            </div>
          </>
        ) : null
      }>
        <Outlet />
      </LayoutBody>
      </GlobalPreviewProvider>

      <ToastContainer />
    </div>
  );
}
